'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ADDED_TO_BASKET_MODAL_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useModalActions } from '@headless-workspace/core-ui';
import { ProductTile } from '@headless-workspace/glow-ds';
import { useEngraveContext } from '../../../client';
import { useBasketMutations } from '../../../client/lib/basket/useBasketMutations';

export const useAddToBasketMonoSkuBusinessRules = () => {
    const { add: addToBasket, isMutating: isAddingToBasket } = useBasketMutations();
    const { close: closeMenuModal } = useModalActions();

    const t = useTranslations('Discover.Pdp');
    const { notifySuccess, notifyError } = useToasts();
    const { engraveFormValues } = useEngraveContext();

    const [currentSku, setCurrentSku] = useState<string | null>(null);

    const handleAddToBasket = useCallback(
        async (product: ProductTile) => {
            if (isAddingToBasket) return;

            try {
                setCurrentSku(product.sku);

                const addToBasketResult = await addToBasket([{ id: product.sku, engrave: engraveFormValues }]);
                const customerBasket = unwrapResultSuccess(addToBasketResult);

                if (customerBasket) {
                    closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
                    notifySuccess(t('information.basket.toast.label.success'));
                } else {
                    throw new Error();
                }
            } catch (error) {
                closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
                notifyError(t('information.basket.toast.label.error'));
            } finally {
                setCurrentSku(null);
            }
        },
        [isAddingToBasket, addToBasket, engraveFormValues, closeMenuModal, notifySuccess, t, notifyError],
    );

    return {
        handleAddToBasket,
        currentSku,
        isAddingToBasket: (sku: string) => isAddingToBasket && currentSku === sku,
    };
};
